import { memo, useCallback, useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from 'react'
import { AlertTriangle, Check, ChevronRight } from 'lucide-react'
import type { Series, TemplateRetimeChange, TemplateRetimeResult, TemplateRuleType, TemplateSlot } from '../../types/domain.js'
import { ModalShell } from '../common/ModalShell.js'

export type TemplateSlotDraft = Omit<
  TemplateSlot,
  'id' | 'templateId' | 'createdAt' | 'updatedAt' | 'fixedProgram' | 'series' | 'liveSource'
>

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

function minutes(value: string) {
  const [hours, mins] = value.split(':').map(Number)
  return hours * 60 + mins
}

function time(value: number) {
  return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}:00`
}

function overlaps(slot: Pick<TemplateSlot, 'dayOffset' | 'daySpan' | 'timeOfDay' | 'nominalDurationSec'>, other: Pick<TemplateSlot, 'dayOffset' | 'daySpan' | 'timeOfDay' | 'nominalDurationSec'>) {
  const slotEndDay = slot.dayOffset + slot.daySpan
  const otherEndDay = other.dayOffset + other.daySpan
  if (slot.dayOffset >= otherEndDay || other.dayOffset >= slotEndDay) return false
  const start = minutes(slot.timeOfDay)
  const end = start + slot.nominalDurationSec / 60
  const otherStart = minutes(other.timeOfDay)
  const otherEnd = otherStart + other.nominalDurationSec / 60
  return start < otherEnd && end > otherStart
}

const DayPicker = memo(function DayPicker({ selected, onChange }: { selected: number[]; onChange: (days: number[]) => void }) {
  return <div className="template-day-picker">{DAYS.map((day, index) => <DayButton key={day} day={day} index={index} selected={selected} onChange={onChange} />)}</div>
})

const DayButton = memo(function DayButton({ day, index, selected, onChange }: { day: string; index: number; selected: number[]; onChange: (days: number[]) => void }) {
  const active = selected.includes(index)
  const handleClick = useCallback(() => onChange(active ? selected.filter(value => value !== index) : [...selected, index]), [active, index, onChange, selected])
  return <button type="button" className={`tp-button${active ? ' tp-button--info' : ''}`} onClick={handleClick} aria-pressed={active}>{day}</button>
})

const Field = memo(function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="template-field"><span className="tp-field-label">{label}</span>{children}</label>
})

export const TemplateSmartFillDialog = memo(function TemplateSmartFillDialog(props: {
  slots: TemplateSlot[]
  series: Series[]
  onApply: (slots: TemplateSlotDraft[]) => Promise<void>
  onClose: () => void
}) {
  const [ruleType, setRuleType] = useState<TemplateRuleType>('SMART_RERUN')
  const [seriesId, setSeriesId] = useState(props.series[0]?.id ?? '')
  const [days, setDays] = useState([0, 1, 2, 3, 4, 5, 6])
  const [start, setStart] = useState('08:00')
  const [end, setEnd] = useState('23:00')
  const [duration, setDuration] = useState(60)
  const [gap, setGap] = useState(2)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const drafts = useMemo(() => {
    if (!seriesId || !days.length || duration <= 0 || minutes(end) <= minutes(start)) return []
    const result: TemplateSlotDraft[] = []
    for (const dayOffset of days) {
      for (let cursor = minutes(start); cursor + duration <= minutes(end); cursor += duration + Math.max(0, gap)) {
        const draft: TemplateSlotDraft = {
          dayOffset,
          daySpan: 1,
          timeOfDay: time(cursor),
          nominalDurationSec: duration * 60,
          episodeSpacingSec: 0,
          ruleType,
          airingKind: ruleType === 'PREMIERE' ? 'PREMIERE' : 'RERUN',
          contentAlignment: 'START',
          fixedProgramId: null,
          seriesId,
          liveSourceId: null,
          liveStartMode: 'AUTO',
          liveEndMode: 'AUTO',
          preRollProgramId: null,
          preRollAdSpotId: null,
          postRollProgramId: null,
          postRollAdSpotId: null,
          minRerunGapDays: null,
          maxRerunsTotal: null,
          cycleWeeks: [],
          anchored: false,
          mirrorSlotId: null,
          sortOrder: props.slots.length + result.length,
        }
        if (![...props.slots, ...result].some(slot => overlaps(slot, draft))) result.push(draft)
      }
    }
    return result
  }, [days, duration, end, gap, props.slots, ruleType, seriesId, start])

  const apply = useCallback(async () => {
    if (!drafts.length) {
      setError('Aucun emplacement libre ne correspond à ces paramètres.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await props.onApply(drafts)
      props.onClose()
    } catch (cause: any) {
      setError(cause?.message ?? 'Le remplissage a échoué.')
    } finally {
      setBusy(false)
    }
  }, [drafts, props])
  const handleRuleTypeChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => setRuleType(event.target.value as TemplateRuleType), [])
  const handleSeriesIdChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => setSeriesId(event.target.value), [])
  const handleStartChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setStart(event.target.value), [])
  const handleEndChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setEnd(event.target.value), [])
  const handleDurationChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setDuration(Number(event.target.value)), [])
  const handleGapChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setGap(Number(event.target.value)), [])

  return (
    <ModalShell title="Grille intelligente du modèle" onClose={props.onClose} size="wide">
      <div className="tp-modal-body template-assist">
        <p className="template-assist__description">Crée en série des règles automatiques sans toucher à une grille diffusée. Les créneaux qui chevaucheraient le modèle actuel sont ignorés.</p>
        <Field label="Règle appliquée">
          <div className="template-assist__source">
            <select className="tp-select" value={ruleType} onChange={handleRuleTypeChange}>
              <option value="PREMIERE">Inédit</option>
              <option value="PREMIERE_RERUN">Rediffusion de l’inédit</option>
              <option value="SMART_RERUN">Rediffusion</option>
              <option value="DESCENDING_RERUN">Rediffusion antichronologique</option>
            </select>
            <select className="tp-select" value={seriesId} onChange={handleSeriesIdChange}>
              <option value="">Sélectionner une série…</option>
              {props.series.map(item => <option key={item.id} value={item.id}>{item.title}</option>)}
            </select>
          </div>
        </Field>
        <Field label="Jours"><DayPicker selected={days} onChange={setDays} /></Field>
        <div className="tp-form-grid tp-form-grid--four">
          <Field label="De"><input className="tp-input tp-input--data" type="time" value={start} onChange={handleStartChange} /></Field>
          <Field label="À"><input className="tp-input tp-input--data" type="time" value={end} onChange={handleEndChange} /></Field>
          <Field label="Durée (min)"><input className="tp-input tp-input--data" type="number" min="1" value={duration} onChange={handleDurationChange} /></Field>
          <Field label="Intervalle (min)"><input className="tp-input tp-input--data" type="number" min="0" value={gap} onChange={handleGapChange} /></Field>
        </div>
        <p className="template-assist__summary">{drafts.length} créneau(x) seront ajoutés. Les trous laissés entre eux seront comblés sur la grille de diffusion.</p>
        {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}
        <div className="tp-modal-actions">
          <button type="button" className="tp-button" onClick={props.onClose}>Annuler</button>
          <button type="button" className="tp-button tp-button--primary" disabled={busy || !drafts.length} data-state={busy ? 'loading' : undefined} onClick={apply}>Ajouter les règles</button>
        </div>
      </div>
    </ModalShell>
  )
})

const RETIME_DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']

/**
 * Une durée de grille se lit en minutes. Les seules qui ne tombent pas juste
 * sont les fenêtres rabotées faute de place : leurs secondes sont dites, parce
 * que c'est exactement là que l'opérateur arbitre.
 */
function durationLabel(seconds: number) {
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return rest === 0 ? `${minutes} min` : `${minutes} min ${String(rest).padStart(2, '0')} s`
}

/**
 * Un décalage de quelques secondes est un décalage : couper les secondes
 * afficherait « 14:30 → 14:30 » pour le créneau que le jingle de tête pousse de
 * dix secondes. Elles n'apparaissent donc que lorsqu'elles portent le sens.
 */
function timeLabel(time: string, other: string) {
  return time.endsWith(':00') && other.endsWith(':00') ? time.slice(0, 5) : time
}

/**
 * Recalage du modèle sur les durées réelles du catalogue. Rien n'est écrit tant
 * que l'opérateur n'a pas appliqué : l'ouverture ne fait qu'un plan, et chaque
 * changement de rendez-vous figé le recalcule.
 */
export const TemplateRetimeDialog = memo(function TemplateRetimeDialog(props: {
  slots: TemplateSlot[]
  onPlan: (anchorSlotIds: string[], apply: boolean) => Promise<TemplateRetimeResult>
  onApplied: () => Promise<void> | void
  onClose: () => void
}) {
  const anchorCandidates = useMemo(
    () => props.slots.filter(slot => slot.ruleType === 'PREMIERE')
      .sort((left, right) => left.dayOffset - right.dayOffset || left.timeOfDay.localeCompare(right.timeOfDay)),
    [props.slots],
  )
  const [anchorIds, setAnchorIds] = useState<string[]>(() => anchorCandidates.map(slot => slot.id))
  const [result, setResult] = useState<TemplateRetimeResult | null>(null)
  const [busy, setBusy] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [applied, setApplied] = useState(false)

  useEffect(() => {
    let cancelled = false
    setBusy(true)
    props.onPlan(anchorIds, false)
      .then(plan => { if (!cancelled) { setResult(plan); setError(null) } })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Recalage impossible') })
      .finally(() => { if (!cancelled) setBusy(false) })
    return () => { cancelled = true }
  }, [anchorIds, props.onPlan])

  const toggleAnchor = useCallback((slotId: string) => {
    setAnchorIds(current => current.includes(slotId) ? current.filter(id => id !== slotId) : [...current, slotId])
  }, [])

  const apply = useCallback(async () => {
    setBusy(true)
    try {
      const outcome = await props.onPlan(anchorIds, true)
      setResult(outcome)
      setApplied(outcome.applied)
      if (outcome.applied) await props.onApplied()
      if (!outcome.applied) setError('Le modèle projeté serait invalide : rien n’a été écrit.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Recalage impossible')
    } finally {
      setBusy(false)
    }
  }, [anchorIds, props])

  const moved = result?.changes.filter(change => change.fromTimeOfDay !== change.toTimeOfDay) ?? []
  const byDay = useMemo(() => {
    const groups = new Map<number, TemplateRetimeChange[]>()
    for (const change of result?.changes ?? []) {
      const list = groups.get(change.dayOffset) ?? []
      list.push(change)
      groups.set(change.dayOffset, list)
    }
    return [...groups.entries()].sort((left, right) => left[0] - right[0])
  }, [result])

  return (
    <ModalShell title="Recaler le modèle sur les durées réelles" size="wide" className="template-retime" onClose={props.onClose} closeDisabled={busy}>
      <div className="template-retime__body">
        <p className="template-retime__intro">
          Chaque fenêtre est ramenée sur ce que son contenu dure vraiment — moyenne des éditions de la série, bloc
          de parties pour un inédit. Les créneaux qui empiétaient sont ensuite décalés juste assez pour ne plus
          déborder ; les fenêtres qui enchaînent plusieurs épisodes les gardent tous.
        </p>

        {result && <div className="template-retime__summary">
          <RetimeCount value={result.changes.length} label="créneaux recalés" />
          <RetimeCount value={moved.length} label="heures de début décalées" />
          <RetimeCount value={result.issues.length} label="arbitrages restants" tone={result.issues.length > 0 ? 'warning' : undefined} />
        </div>}

        <details className="template-retime__anchors">
          <summary>
            <ChevronRight size={14} aria-hidden="true" />
            <span>Rendez-vous figés</span>
            <span className="template-retime__pill"><Check size={12} aria-hidden="true" /> {anchorIds.length} / {anchorCandidates.length} tenus</span>
          </summary>
          <p>Leur heure de début ne bouge pas. Décochez-en un pour autoriser le recalage à le déplacer.</p>
          <div className="template-retime__anchor-list">
            {anchorCandidates.map(slot => (
              <AnchorToggle key={slot.id} slot={slot} checked={anchorIds.includes(slot.id)} onToggle={toggleAnchor} />
            ))}
          </div>
        </details>

        {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}
        {applied && <div className="tp-feedback tp-feedback--success" role="status">
          Modèle recalé{result?.validation?.valid === false ? ' — contrôle à revoir avant de prévisualiser.' : ' et revalidé.'}
        </div>}

        {result && result.issues.length > 0 && <section className="template-retime__issues" aria-label="Arbitrages restants">
          <h3><AlertTriangle size={13} aria-hidden="true" /> À arbitrer à la main <span>{result.issues.length}</span></h3>
          {result.issues.map(issue => (
            <div key={`${issue.slotId}-${issue.reason}`} className="template-retime__issue">
              <span className="template-retime__issue-when">{issue.label.split(' · ')[0]}</span>
              <div>
                <strong>{issue.label.split(' · ').slice(1).join(' · ')}</strong>
                <p>{issue.reason}</p>
              </div>
            </div>
          ))}
        </section>}

        {result && result.changes.length > 0 && <div className="template-retime__table" role="table" aria-label="Créneaux recalés">
          <div className="template-retime__head" role="row">
            <span role="columnheader">Heure</span>
            <span role="columnheader">Programme</span>
            <span role="columnheader">Début</span>
            <span role="columnheader">Durée de la fenêtre</span>
            <span />
          </div>
          {byDay.map(([dayOffset, changes]) => (
            <section key={dayOffset}>
              <h3>{RETIME_DAYS[dayOffset]} <span>· {changes.length}</span></h3>
              {changes.map(change => <RetimeRow key={change.slotId} change={change} />)}
            </section>
          ))}
        </div>}
        {result && result.changes.length === 0 && !busy && (
          <div className="tp-empty-state">Le modèle est déjà calé sur les durées du catalogue.</div>
        )}
      </div>

      <div className="tp-modal__footer template-retime__footer">
        <p>Rien n’est enregistré tant que le recalage n’est pas appliqué.</p>
        <div>
          <button type="button" className="tp-button" onClick={props.onClose} disabled={busy}>{applied ? 'Fermer' : 'Annuler'}</button>
          {!applied && <button
            type="button"
            className="tp-button tp-button--primary"
            onClick={apply}
            disabled={busy || !result || result.changes.length === 0}
            data-state={busy ? 'loading' : undefined}
          >
            Appliquer le recalage
          </button>}
        </div>
      </div>
    </ModalShell>
  )
})

const RetimeCount = memo(function RetimeCount(props: { value: number; label: string; tone?: 'warning' }) {
  return (
    <div className={`template-retime__count${props.tone ? ` is-${props.tone}` : ''}`}>
      <strong>{props.value}</strong>
      <span>{props.label}</span>
    </div>
  )
})

/**
 * Une ligne du relevé. L'heure ne montre une flèche que si le créneau bouge — un
 * « 18:20 → 18:20 » ferait chercher un changement qui n'existe pas — et la
 * pastille de delta dit en minutes ce que la fenêtre gagne ou perd.
 */
const RetimeRow = memo(function RetimeRow({ change }: { change: TemplateRetimeChange }) {
  const [when, ...rest] = change.label.split(' · ')
  const deltaMin = Math.round((change.toNominalDurationSec - change.fromNominalDurationSec) / 60)
  const unchanged = change.toNominalDurationSec === change.fromNominalDurationSec
  return (
    <div className="template-retime__row" role="row">
      <span className="template-retime__when">{when.split(' ')[1] ?? when}</span>
      <span className="template-retime__label">{rest.join(' · ')}</span>
      <span className="template-retime__start">
        {change.fromTimeOfDay === change.toTimeOfDay ? '—' : <>
          <span className="is-before">{timeLabel(change.fromTimeOfDay, change.toTimeOfDay)}</span>
          {' → '}
          <strong>{timeLabel(change.toTimeOfDay, change.fromTimeOfDay)}</strong>
        </>}
      </span>
      <span className="template-retime__window">
        {unchanged ? <>
          <strong>{durationLabel(change.toNominalDurationSec)}</strong> <em>inchangée</em>
        </> : <>
          <span className="is-before">{durationLabel(change.fromNominalDurationSec)}</span>
          {' → '}
          <strong>{durationLabel(change.toNominalDurationSec)}</strong>
          <span className={`template-retime__delta${deltaMin > 0 ? ' is-longer' : ''}`}>{deltaMin > 0 ? `+${deltaMin}` : deltaMin}</span>
        </>}
      </span>
      <span className="template-retime__tags">
        {change.episodes > 1 && <span className="template-retime__tag">{change.episodes} épisodes</span>}
        {change.overflowRisk && <span
          className="template-retime__tag is-warning"
          title="La plus longue édition de la série dépasse encore cette fenêtre"
        >édition longue</span>}
      </span>
    </div>
  )
})

const AnchorToggle = memo(function AnchorToggle(props: {
  slot: TemplateSlot
  checked: boolean
  onToggle: (slotId: string) => void
}) {
  const handleChange = useCallback(() => props.onToggle(props.slot.id), [props])
  return (
    <label className="template-retime__anchor">
      <input type="checkbox" checked={props.checked} onChange={handleChange} />
      <span>{DAYS[props.slot.dayOffset]} {props.slot.timeOfDay.slice(0, 5)} · {props.slot.series?.title ?? props.slot.fixedProgram?.title ?? 'inédit'}</span>
    </label>
  )
})
